// src/components/Layout.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import PHLogo from "../Images/PharmacyHalo_Logo.png";

const Layout = ({ children, setIsAuthenticated }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onResize = () => setIsOpen(window.innerWidth >= 1024);
    onResize();
    window.addEventListener("resize", onResize);

    const storedUserInfo = localStorage.getItem("googleUserInfo");
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        setUserInfo(parsedUserInfo);
        setImageError(false); // Reset image error when user info changes
      } catch (e) {
        console.error("Error parsing user info:", e);
      }
    }
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLogout = () => {
    try {
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("googleUserInfo");
      localStorage.removeItem("userData");
      localStorage.removeItem("tempGoogleUserInfo");
      localStorage.removeItem("token");
      Object.keys(localStorage).forEach((k) => {
        if (/(token|auth|user|google)/i.test(k)) localStorage.removeItem(k);
      });
    } finally {
      setIsAuthenticated?.(false);
      navigate("/login", { replace: true });
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) setIsOpen(false);
  };

  const sidebarItems = [
    {
      text: "Overview",
      id: "overview",
      path: "/overview",
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5">
          <path
            fill="currentColor"
            d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"
          />
        </svg>
      ),
    },
    {
      text: "Quda Overview",
      id: "quda",
      path: "/quda",
      icon: <FactCheckIcon className="w-5 h-5" />,
    },
    {
      text: "DocLink Overview",
      id: "doclink",
      path: "/doclink",
      icon: <MedicationOutlinedIcon className="w-5 h-5" />,
    },
    {
      text: "Subscriptions",
      id: "subscriptions",
      path: "/subs",
      icon: <SubscriptionsIcon className="w-5 h-5" />,
    },
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 lg:hidden z-30"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`fixed z-40 inset-y-0 left-0 w-64 bg-white shadow-xl border-r
                    transform transition-transform duration-200 ease-out
                    ${isOpen ? "translate-x-0" : "-translate-x-full"}
                    lg:static lg:translate-x-0`}
        aria-label="Sidebar"
      >
        <div className="h-full flex flex-col">
          <div className="px-4 py-4 border-b flex items-center justify-center">
            <img
              src={PHLogo}
              alt="Pharmacy Halo Logo"
              className="w-3/4 h-auto object-contain"
            />
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {sidebarItems.map((item) => {
              const active = location.pathname === item.path;

              const getActiveStyles = (id) => {
                switch (id) {
                  case "overview":
                    return "bg-blue-100 text-[#002169]";
                  case "quda":
                    return "bg-[#c9e9ea]/50 text-[#00827b]";
                  case "doclink":
                    return "bg-[#F3E8FF] text-[#401d5e]";
                    case "subscriptions":
                    return "bg-blue-100 text-[#002169]";
                  default:
                    return "bg-teal-50 text-teal-700";
                }
              };

              const getHoverStyles = (id) => {
                switch (id) {
                  case "overview":
                    return "hover:bg-blue-100 hover:text-[#002169]";
                  case "quda":
                    return "hover:bg-[#c9e9ea]/50 hover:text-[#00827b]";
                  case "doclink":
                    return "hover:bg-[#F3E8FF] hover:text-[#401d5e]";
                  case "subscriptions":
                    return "hover:bg-blue-100 hover:text-[#002169]";
                  default:
                    return "hover:bg-teal-50 hover:text-teal-700";
                }
              };

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-3 mx-0 rounded-lg text-left transition-all duration-200 group
                             ${
                               active
                                 ? getActiveStyles(item.id)
                                 : `text-gray-600 ${getHoverStyles(item.id)}`
                             }`}
                >
                  <span className={`${active ? "text-current" : "text-gray-400 group-hover:text-current"}`}>
                    {item.icon}
                  </span>
                  <span className="font-medium">{item.text}</span>
                </button>
              );
            })}
          </nav>

          <div className="p-3 border-t space-y-2">
            {/* User Info */}
            <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
              {userInfo?.picture && !imageError ? (
                <img
                  src={userInfo.picture}
                  alt={`${userInfo.name || "User"}'s profile`}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    setImageError(true);
                  }}
                  onLoad={() => {}}
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-teal-400 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : "U"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {userInfo?.name || "User"}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {userInfo?.email || ""}
                </p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-red-600 hover:bg-red-50 transition"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5">
                <path
                  fill="currentColor"
                  d="M16 13v-2H7V7l-5 5 5 5v-4h9zM20 3h-8v2h8v14h-8v2h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"
                />
              </svg>
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main column must be a flex column with min-h-0 so children can scroll and no phantom space */}
      <div className="flex-1 flex flex-col min-h-0">
        <header className="bg-white shadow-sm border-b px-4 py-2 flex items-center sticky top-0 z-20">
          <button
            onClick={() => setIsOpen((s) => !s)}
            className="p-2 rounded-lg hover:bg-gray-100 transition lg:hidden"
            aria-label="Toggle sidebar"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6">
              <path
                fill="currentColor"
                d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zM3 6v2h18V6H3z"
              />
            </svg>
          </button>
        </header>

        {/* Content area */}
        <main className="flex-1 overflow-auto p-4">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
